import { useState, useCallback, createContext, useContext, type ReactNode } from 'react';

type ToastVariant = 'success' | 'error' | 'info' | 'warning';
interface ToastItem { id: number; message: string; variant: ToastVariant; exiting?: boolean }

interface ToastContextType { toast: (message: string, variant?: ToastVariant) => void }
const ToastContext = createContext<ToastContextType>({ toast: () => {} });

export function useToast() { return useContext(ToastContext); }

const ICONS: Record<ToastVariant, string> = {
  success: '✓',
  error: '✕',
  info: 'ℹ',
  warning: '⚠',
};

const COLORS: Record<ToastVariant, { bg: string; border: string; text: string; icon: string; iconBg: string }> = {
  success: { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534', icon: '#fff', iconBg: '#16a34a' },
  error:   { bg: '#fef2f2', border: '#fecaca', text: '#991b1b', icon: '#fff', iconBg: '#dc2626' },
  info:    { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af', icon: '#fff', iconBg: '#2563eb' },
  warning: { bg: '#fffbeb', border: '#fde68a', text: '#92400e', icon: '#fff', iconBg: '#d97706' },
};

const DURATIONS: Record<ToastVariant, number> = {
  success: 4000,
  error: 7000,
  info: 4000,
  warning: 5000,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  let nextId = 0;

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 300);
  }, []);

  const toast = useCallback((message: string, variant: ToastVariant = 'info') => {
    const id = ++nextId;
    setToasts(prev => [...prev, { id, message, variant }]);
    setTimeout(() => dismiss(id), DURATIONS[variant]);
  }, [dismiss]);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div style={{
        position: 'fixed', top: '16px', right: '16px', zIndex: 150,
        display: 'flex', flexDirection: 'column', gap: '8px',
        pointerEvents: 'none', maxWidth: '420px',
      }}>
        {toasts.map(t => {
          const c = COLORS[t.variant];
          return (
            <div key={t.id} style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              background: c.bg, border: `1px solid ${c.border}`,
              borderRadius: '10px', padding: '10px 14px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.1), 0 1px 4px rgba(0,0,0,0.06)',
              color: c.text, fontSize: '13px', fontWeight: 500,
              lineHeight: '1.4', pointerEvents: 'auto',
              animation: t.exiting ? 'toastOut 0.3s ease-in forwards' : 'toastIn 0.25s ease-out',
              cursor: 'pointer', minWidth: '240px',
            }} onClick={() => dismiss(t.id)}>
              <div style={{
                width: '22px', height: '22px', borderRadius: '50%',
                background: c.iconBg, color: c.icon,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', fontWeight: 700, flexShrink: 0,
              }}>
                {ICONS[t.variant]}
              </div>
              <div style={{ flex: 1 }}>{t.message}</div>
              <div style={{ fontSize: '16px', opacity: 0.4, flexShrink: 0, marginLeft: '4px', lineHeight: 1 }}>×</div>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateX(40px) scale(0.95); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes toastOut {
          from { opacity: 1; transform: translateX(0) scale(1); }
          to { opacity: 0; transform: translateX(40px) scale(0.95); }
        }
      `}</style>
    </ToastContext.Provider>
  );
}

export default function Toast() { return null; }
